import React, { FunctionComponent, useCallback, useState } from 'react';
import { useForm } from 'react-hook-form';
import styled from 'styled-components';

import { ScreenReaderUpdateText, SaveEmailSubmitButtonGroup } from 'components';
import { FormGroup } from 'components/chroma';
import {
  getCoverType,
  getIncludedExtras,
  getIsPortableContentsExtraSelected,
  getSelectedExtras,
  hasContentsCoverType,
  hasSpecifiedPortableContentsAustraliaWide,
  hasUnspecifiedPortableContents,
} from 'scenes/home/selectors';
import { HomeFullPageProps } from 'scenes/home/types';
import { availableExtras } from 'scenes/home/helpers/OptionalExtras/availableExtras';
import OptionalExtrasList from 'scenes/common/components/Steps/OptionalExtrasStep/OptionalExtrasList';
import { OptionalExtraCard } from 'scenes/common/components/Steps/OptionalExtrasStep/OptionalExtraCard';
import FindOutMoreModal from 'scenes/common/components/Steps/OptionalExtrasStep/FindOutMoreModal/FindOutMoreModal';
import { portableContents } from 'scenes/home/helpers/OptionalExtras/portableContentsCover';
import PortableContentsModal from './PortableContentsModal';

const PortableContentsExtraWrapper = styled.div`
  margin-top: 0.7rem;
`;

const EXTRAS_ERROR = 'extras-error';
export const QUESTION = 'Add extras to your policy';

const OptionalExtrasStep: FunctionComponent<HomeFullPageProps> = ({
  funnelState,
  onNext,
  onUpdateOptionalExtras,
  onClickSaveQuoteModal,
  onSelectPortableContentsExtra,
}) => {
  const {
    formState: { isValid },
    errors,
    handleSubmit,
  } = useForm({
    mode: 'onChange',
  });

  const [showOptionalExtraModal, setShowOptionalExtraModal] = useState(false);
  const [showPortableContentsModal, setShowPortableContentsModal] = useState(
    false
  );
  const [
    isPortableContentsExtraSelected,
    setIsPortableContentsExtraSelected,
  ] = useState(getIsPortableContentsExtraSelected(funnelState));

  const availableExtrasList = availableExtras[getCoverType(funnelState)];
  const selectedExtras = getSelectedExtras(funnelState);
  const includedExtras = getIncludedExtras(funnelState);
  const hasPortableContents =
    hasSpecifiedPortableContentsAustraliaWide(funnelState) ||
    hasUnspecifiedPortableContents(funnelState);

  const handleSelect = useCallback(
    (optionalExtra: string): void => {
      if (includedExtras?.some((option) => option === optionalExtra)) {
        return;
      }

      const copySelectedExtras = [...selectedExtras];
      const foundIndex = selectedExtras?.findIndex(
        (extra) => extra === optionalExtra
      );

      if (foundIndex > -1) {
        copySelectedExtras.splice(foundIndex, 1);
      } else {
        copySelectedExtras.push(optionalExtra);
      }

      onUpdateOptionalExtras(copySelectedExtras);
    },
    [includedExtras, selectedExtras, onUpdateOptionalExtras]
  );

  const handlePortableContentsClick = useCallback(() => {
    if (hasPortableContents) {
      setShowPortableContentsModal(true);
    } else {
      setIsPortableContentsExtraSelected(!isPortableContentsExtraSelected);
      onSelectPortableContentsExtra();
    }
  }, [
    hasPortableContents,
    isPortableContentsExtraSelected,
    onSelectPortableContentsExtra,
  ]);

  return (
    <form
      onSubmit={handleSubmit(() =>
        onNext({
          isPortableContentsExtraSelected,
        })
      )}
    >
      <FormGroup
        legend={QUESTION}
        error={errors[EXTRAS_ERROR]?.message || ''}
        helpLink={{
          id: 'find-out-more-link',
          text: 'Find out more about policy extras',
          ariaLabel: 'Find out more about policy extras - Opens dialog',
          onClick: () => {
            setShowOptionalExtraModal(true);
          },
        }}
      >
        <ScreenReaderUpdateText />
        <OptionalExtrasList
          availableExtras={availableExtrasList}
          includedExtras={includedExtras}
          selectedExtras={selectedExtras}
          onClick={handleSelect}
          numberOfOptions={
            // If cover type includes contents, add one to the number of options for the portable contents extra
            hasContentsCoverType(funnelState)
              ? availableExtrasList.length + 1
              : availableExtrasList.length
          }
        />
        {hasContentsCoverType(funnelState) ? (
          <PortableContentsExtraWrapper>
            <OptionalExtraCard
              image={portableContents.image}
              label={portableContents.label}
              additionalText=""
              selected={isPortableContentsExtraSelected}
              onClick={handlePortableContentsClick}
              screenReaderText={`Option ${availableExtrasList.length + 1} of ${
                availableExtrasList.length + 1
              }`}
            />
          </PortableContentsExtraWrapper>
        ) : (
          <></>
        )}
      </FormGroup>
      <SaveEmailSubmitButtonGroup
        onClickSaveQuoteModal={onClickSaveQuoteModal}
        submitEnabled={isValid}
      />
      <FindOutMoreModal
        optionalExtras={[
          ...availableExtrasList,
          ...(hasContentsCoverType(funnelState) ? [portableContents] : []),
        ]}
        isOpen={showOptionalExtraModal}
        onClose={() => setShowOptionalExtraModal(false)}
      />
      <PortableContentsModal
        isOpen={showPortableContentsModal}
        onClose={() => setShowPortableContentsModal(false)}
      />
    </form>
  );
};

export default OptionalExtrasStep;
